import { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Search,
  Star,
  User,
  Phone,
  Mail,
  Briefcase,
  Award,
  Edit,
  Trash2,
  X,
  Check,
  Users,
  Sparkles,
  Crown,
  Clock,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Save,
  Copy,
  Zap,
  Shield,
  ChevronDown,
  ChevronUp,
  GripVertical,
  FileText,
  BookOpen,
  Camera
} from 'lucide-react';
import { toast } from 'sonner';

const expertiseOptions = [
  'Framing', 'Drywall', 'Tile', 'Paint', 'Flooring', 'Plumbing', 'Electrical',
  'HVAC', 'Roofing', 'Trim/Carpentry', 'Cabinets', 'Countertops', 'Demo'
];

const temperamentOptions = [
  'Detail-Oriented', 'Fast Worker', 'Problem Solver', 'Team Player',
  'Independent', 'Creative', 'Reliable', 'Calm Under Pressure', 'Leader'
];

const roleOptions = [
  'Team Lead', 'Foreman', 'Specialist', 'Apprentice', 'Subcontractor'
];

const expertiseLevelOptions = [
  { value: 'apprentice', label: 'Apprentice', description: '< 2 years' },
  { value: 'journeyman', label: 'Journeyman', description: '2-7 years' },
  { value: 'master', label: 'Master', description: '8-15 years' },
  { value: 'expert', label: 'Expert', description: '15+ years' }
];

export const TeamBuilder = () => {
  const { api } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterExpertise, setFilterExpertise] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  
  // Team Builder State
  const [selectedJob, setSelectedJob] = useState(null);
  const [teamLead, setTeamLead] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamPreview, setTeamPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [weightingMode, setWeightingMode] = useState('combined');
  
  // Templates State
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [employeeViewMode, setEmployeeViewMode] = useState('simple'); // 'simple' or 'advanced'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, jobsRes, templatesRes] = await Promise.all([
        api.get('/employees/'),
        api.get('/jobs/'),
        api.get('/employees/templates/list').catch(() => ({ data: [] }))
      ]);
      setEmployees(empRes.data);
      setJobs(jobsRes.data.filter(j => ['active', 'pursuing', 'bid_submitted'].includes(j.status)));
      setTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch team preview when team changes
  const fetchTeamPreview = useCallback(async () => {
    if (!selectedJob || (!teamLead && teamMembers.length === 0)) {
      setTeamPreview(null);
      return;
    }
    
    setPreviewLoading(true);
    try {
      const response = await api.post(`/employees/${selectedJob}/team/preview`, {
        team_lead_id: teamLead?.id || null,
        member_ids: teamMembers.map(m => m.id),
        weighting_mode: weightingMode
      });
      setTeamPreview(response.data);
    } catch (error) {
      console.error('Error fetching team preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedJob, teamLead, teamMembers, weightingMode, api]);

  useEffect(() => {
    const timeoutId = setTimeout(fetchTeamPreview, 300);
    return () => clearTimeout(timeoutId);
  }, [fetchTeamPreview]);

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          emp.role.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesExpertise = !filterExpertise || emp.expertise?.includes(filterExpertise);
    return matchesSearch && matchesExpertise && emp.is_active !== false;
  });

  const availableEmployees = filteredEmployees.filter(emp => 
    !emp.current_job_id && 
    emp.id !== teamLead?.id && 
    !teamMembers.some(m => m.id === emp.id)
  );

  const assignedEmployees = filteredEmployees.filter(emp => emp.current_job_id);

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    
    if (!destination) return;
    
    const employee = employees.find(e => e.id === draggableId);
    if (!employee) return;
    
    // Remove from current location
    if (source.droppableId === 'team-lead') {
      setTeamLead(null);
    } else if (source.droppableId === 'team-members') {
      setTeamMembers(prev => prev.filter(m => m.id !== draggableId));
    }
    
    // Add to new location
    if (destination.droppableId === 'team-lead') {
      // If there's already a lead, move them to members
      if (teamLead) {
        setTeamMembers(prev => [...prev, teamLead]);
      }
      setTeamLead(employee);
    } else if (destination.droppableId === 'team-members') {
      if (!teamMembers.some(m => m.id === employee.id)) {
        setTeamMembers(prev => [...prev, employee]);
      }
    } else if (destination.droppableId === 'available') {
      // Already removed above, just refresh
    }
  };

  const handleSaveEmployee = async (employeeData) => {
    try {
      if (editingEmployee) {
        await api.patch(`/employees/${editingEmployee.id}`, employeeData);
        toast.success('Employee updated');
      } else {
        await api.post('/employees/', employeeData);
        toast.success('Employee added');
      }
      fetchData();
      setShowAddModal(false);
      setEditingEmployee(null);
    } catch (error) {
      toast.error('Failed to save employee');
    }
  };

  const deleteEmployee = async (empId) => {
    if (!window.confirm('Are you sure you want to remove this employee?')) return;
    try {
      await api.delete(`/employees/${empId}`);
      setEmployees(employees.filter(e => e.id !== empId));
      toast.success('Employee removed');
    } catch (error) {
      toast.error('Failed to remove employee');
    }
  };

  const assignTeamToJob = async () => {
    if (!selectedJob) {
      toast.error('Please select a job');
      return;
    }
    if (!teamLead && teamMembers.length === 0) {
      toast.error('Please add at least one team member');
      return;
    }
    
    try {
      const response = await api.post(`/employees/${selectedJob}/team`, {
        team_lead_id: teamLead?.id || null,
        member_ids: teamMembers.map(m => m.id),
        weighting_mode: weightingMode
      });
      toast.success(`Team assigned! Grade: ${response.data.team_grade} (${response.data.team_score})`);
      
      // Reset team builder
      setTeamLead(null);
      setTeamMembers([]);
      setSelectedJob(null);
      setTeamPreview(null);
      
      fetchData();
    } catch (error) {
      toast.error('Failed to assign team');
    }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    const job = jobs.find(j => j.id === selectedJob);
    
    try {
      await api.post('/employees/templates', {
        name: templateName,
        team_lead_id: teamLead?.id || null,
        member_ids: teamMembers.map(m => m.id),
        project_type: job?.project_type || null,
        description: `Team for ${job?.project_type || 'General'} projects`
      });
      toast.success('Template saved');
      setShowTemplateModal(false);
      setTemplateName('');
      fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const loadTemplate = async (template) => {
    // Load team lead
    const lead = employees.find(e => e.id === template.team_lead_id);
    if (lead && !lead.current_job_id) {
      setTeamLead(lead);
    }
    
    // Load members (only available ones)
    const members = employees.filter(e => 
      template.member_ids.includes(e.id) && 
      !e.current_job_id &&
      e.id !== template.team_lead_id
    );
    setTeamMembers(members);
    
    toast.success(`Loaded template: ${template.name}`);
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'text-gray-500 bg-gray-100';
    if (grade.startsWith('A')) return 'text-green-600 bg-green-50 border-green-200';
    if (grade.startsWith('B')) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (grade.startsWith('C')) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (grade.startsWith('D')) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-builder">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Team Builder</h1>
          <p className="text-gray-500 mt-1">
            {employees.filter(e => e.is_active !== false).length} team members • 
            {availableEmployees.length} available
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setEmployeeViewMode('simple')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                employeeViewMode === 'simple'
                  ? 'bg-[#C6A87C] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Simple
            </button>
            <button
              onClick={() => setEmployeeViewMode('advanced')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                employeeViewMode === 'advanced'
                  ? 'bg-[#C6A87C] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Advanced
            </button>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
            data-testid="add-employee-btn"
          >
            <Plus className="w-4 h-4" />
            Add Team Member
          </button>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Available Employees */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-100 p-4 mb-4">
              <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#C6A87C]" />
                Available Team Members
              </h2>
              
              {/* Search & Filter */}
              <div className="space-y-3 mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                    data-testid="team-search"
                  />
                </div>
                <select
                  value={filterExpertise}
                  onChange={(e) => setFilterExpertise(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none bg-white"
                >
                  <option value="">All Expertise</option>
                  {expertiseOptions.map(exp => (
                    <option key={exp} value={exp}>{exp}</option>
                  ))}
                </select>
              </div>
              
              {/* Droppable Available List */}
              <Droppable droppableId="available">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`space-y-2 max-h-[500px] overflow-y-auto p-2 rounded transition-colors ${
                      snapshot.isDraggingOver ? 'bg-gray-50' : ''
                    }`}
                  >
                    {availableEmployees.map((emp, index) => (
                      <Draggable key={emp.id} draggableId={emp.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`p-3 border bg-white cursor-grab active:cursor-grabbing transition-all ${
                              snapshot.isDragging 
                                ? 'shadow-lg border-[#C6A87C] rotate-2' 
                                : 'border-gray-100 hover:border-[#C6A87C]/50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <GripVertical className="w-4 h-4 text-gray-300 mt-1 flex-shrink-0" />
                              
                              {/* Profile Picture in Advanced Mode */}
                              {employeeViewMode === 'advanced' && (
                                <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                  {emp.profile_picture_url ? (
                                    <img 
                                      src={emp.profile_picture_url.startsWith('/') 
                                        ? `${process.env.REACT_APP_BACKEND_URL}${emp.profile_picture_url}` 
                                        : emp.profile_picture_url
                                      }
                                      alt={emp.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <User className="w-5 h-5 text-gray-400" />
                                  )}
                                </div>
                              )}
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm truncate">{emp.name}</span>
                                  <span className={`px-1.5 py-0.5 text-xs font-semibold ${getGradeColor(emp.letter_grade)}`}>
                                    {emp.letter_grade || 'N/A'}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500">{emp.role} • {emp.years_experience} yrs</p>
                                
                                {/* Simple View: Basic expertise */}
                                {employeeViewMode === 'simple' && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {emp.expertise?.slice(0, 3).map(exp => (
                                      <span key={exp} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs">
                                        {exp}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Advanced View: Full details */}
                                {employeeViewMode === 'advanced' && (
                                  <div className="mt-2 space-y-2">
                                    {/* Contact Info */}
                                    {(emp.email || emp.phone) && (
                                      <div className="text-xs text-gray-500">
                                        {emp.email && <span className="block truncate">{emp.email}</span>}
                                        {emp.phone && <span className="block">{emp.phone}</span>}
                                      </div>
                                    )}
                                    
                                    {/* Certifications */}
                                    {emp.certifications?.length > 0 && (
                                      <div className="flex flex-wrap gap-1">
                                        {emp.certifications.map(cert => (
                                          <span key={cert} className="px-1.5 py-0.5 bg-green-50 text-green-600 text-xs rounded">
                                            {cert}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    
                                    {/* Expertise */}
                                    <div className="flex flex-wrap gap-1">
                                      {emp.expertise?.map(exp => (
                                        <span key={exp} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs">
                                          {exp}
                                        </span>
                                      ))}
                                    </div>
                                    
                                    {/* Ratings Summary */}
                                    {emp.ratings && (
                                      <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <Star className="w-3 h-3 text-yellow-500" />
                                        <span>
                                          Avg: {(Object.values(emp.ratings).reduce((a, b) => a + b, 0) / Object.values(emp.ratings).length).toFixed(1)}
                                        </span>
                                      </div>
                                    )}
                                    
                                    {/* Emergency Contact Indicator */}
                                    {emp.emergency_contact?.name && (
                                      <div className="text-xs text-orange-600">
                                        Emergency: {emp.emergency_contact.name}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => { setEditingEmployee(emp); setShowAddModal(true); }}
                                className="p-1 text-gray-400 hover:text-[#C6A87C]"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {availableEmployees.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-8">
                        No available employees
                      </p>
                    )}
                  </div>
                )}
              </Droppable>

              {/* Assigned Employees */}
              {assignedEmployees.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs font-semibold uppercase text-gray-400 mb-2">
                    Currently Assigned ({assignedEmployees.length})
                  </p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {assignedEmployees.slice(0, 5).map(emp => (
                      <div key={emp.id} className="p-2 bg-blue-50 border border-blue-100 text-sm">
                        <span className="font-medium">{emp.name}</span>
                        <span className="text-blue-600 text-xs ml-2">On job</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Center Panel - Team Builder */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-100 p-6">
              {/* Job Selection */}
              <div className="mb-6">
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                  Select Job to Assign Team
                </label>
                <select
                  value={selectedJob || ''}
                  onChange={(e) => setSelectedJob(e.target.value || null)}
                  className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
                  data-testid="job-select"
                >
                  <option value="">Choose a job...</option>
                  {jobs.map(job => (
                    <option key={job.id} value={job.id}>
                      {job.client_name} - {job.project_type} ({job.status})
                    </option>
                  ))}
                </select>
              </div>

              {/* Weighting Mode Selector */}
              <div className="mb-6">
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                  Grade Calculation Mode
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { value: 'simple', label: 'Simple Avg', icon: Target },
                    { value: 'weighted', label: 'Lead Weight', icon: Crown },
                    { value: 'task_specific', label: 'Task Match', icon: Zap },
                    { value: 'combined', label: 'Combined', icon: Sparkles }
                  ].map(mode => (
                    <button
                      key={mode.value}
                      onClick={() => setWeightingMode(mode.value)}
                      className={`p-3 border text-sm font-medium transition-colors ${
                        weightingMode === mode.value
                          ? 'border-[#C6A87C] bg-[#C6A87C]/5 text-[#C6A87C]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <mode.icon className="w-4 h-4 mx-auto mb-1" />
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Team Lead Drop Zone */}
              <div className="mb-6">
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                  <Crown className="w-4 h-4 inline mr-1" />
                  Team Lead (Drag here)
                </label>
                <Droppable droppableId="team-lead">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`min-h-[80px] border-2 border-dashed rounded p-4 transition-colors ${
                        snapshot.isDraggingOver 
                          ? 'border-[#C6A87C] bg-[#C6A87C]/5' 
                          : teamLead 
                            ? 'border-[#C6A87C]/30 bg-[#C6A87C]/5'
                            : 'border-gray-200'
                      }`}
                    >
                      {teamLead ? (
                        <Draggable draggableId={teamLead.id} index={0}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="flex items-center gap-4 p-3 bg-[#C6A87C] text-white"
                            >
                              <Crown className="w-5 h-5" />
                              <div className="flex-1">
                                <p className="font-medium">{teamLead.name}</p>
                                <p className="text-sm opacity-80">
                                  {teamLead.role} • {teamLead.years_experience} yrs • Grade: {teamLead.letter_grade || 'N/A'}
                                </p>
                              </div>
                              <button
                                onClick={() => setTeamLead(null)}
                                className="p-1 hover:bg-white/20 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ) : (
                        <p className="text-gray-400 text-sm text-center py-4">
                          Drag an employee here to set as Team Lead
                        </p>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>

              {/* Team Members Drop Zone */}
              <div className="mb-6">
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                  <Users className="w-4 h-4 inline mr-1" />
                  Team Members (Drag here)
                </label>
                <Droppable droppableId="team-members">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`min-h-[120px] border-2 border-dashed rounded p-4 transition-colors ${
                        snapshot.isDraggingOver 
                          ? 'border-blue-400 bg-blue-50' 
                          : teamMembers.length > 0
                            ? 'border-blue-200 bg-blue-50/50'
                            : 'border-gray-200'
                      }`}
                    >
                      {teamMembers.length > 0 ? (
                        <div className="space-y-2">
                          {teamMembers.map((member, index) => (
                            <Draggable key={member.id} draggableId={member.id} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className="flex items-center gap-3 p-3 bg-white border border-gray-200"
                                >
                                  <GripVertical className="w-4 h-4 text-gray-300" />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">{member.name}</span>
                                      <span className={`px-1.5 py-0.5 text-xs font-semibold ${getGradeColor(member.letter_grade)}`}>
                                        {member.letter_grade || 'N/A'}
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                      {member.role} • {member.years_experience} yrs
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => setTeamMembers(prev => prev.filter(m => m.id !== member.id))}
                                    className="p-1 text-gray-400 hover:text-red-500"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-400 text-sm text-center py-8">
                          Drag employees here to add to the team
                        </p>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>

              {/* Team Preview */}
              {teamPreview && (
                <TeamPreviewPanel preview={teamPreview} loading={previewLoading} getGradeColor={getGradeColor} />
              )}

              {/* Templates & Actions */}
              <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t">
                {templates.length > 0 && (
                  <div className="relative group">
                    <button className="px-4 py-2 border border-gray-200 text-sm text-gray-600 hover:border-gray-300 flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      Load Template
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      {templates.map(template => (
                        <button
                          key={template.id}
                          onClick={() => loadTemplate(template)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                        >
                          <p className="font-medium text-sm">{template.name}</p>
                          <p className="text-xs text-gray-500">{template.project_type || 'General'}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {(teamLead || teamMembers.length > 0) && (
                  <button
                    onClick={() => setShowTemplateModal(true)}
                    className="px-4 py-2 border border-gray-200 text-sm text-gray-600 hover:border-gray-300 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save as Template
                  </button>
                )}
                
                <div className="flex-1" />
                
                <button
                  onClick={() => { setTeamLead(null); setTeamMembers([]); setTeamPreview(null); }}
                  className="px-6 py-2 border border-gray-200 text-gray-600 text-sm hover:border-gray-300"
                >
                  Clear Team
                </button>
                
                <button
                  onClick={assignTeamToJob}
                  disabled={!selectedJob || (!teamLead && teamMembers.length === 0)}
                  className="px-6 py-2 bg-[#C6A87C] text-white text-sm font-semibold hover:bg-[#B09265] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="assign-team-btn"
                >
                  <Check className="w-4 h-4" />
                  Assign Team to Job
                </button>
              </div>
            </div>
          </div>
        </div>
      </DragDropContext>

      {/* Add/Edit Employee Modal */}
      <AnimatePresence>
        {showAddModal && (
          <EmployeeModal
            employee={editingEmployee}
            onClose={() => { setShowAddModal(false); setEditingEmployee(null); }}
            onSave={handleSaveEmployee}
            api={api}
          />
        )}
      </AnimatePresence>

      {/* Save Template Modal */}
      <AnimatePresence>
        {showTemplateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowTemplateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white w-full max-w-md p-6"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-heading text-lg mb-4">Save Team Template</h3>
              <input
                type="text"
                placeholder="Template name..."
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 mb-4 focus:border-[#C6A87C] focus:outline-none"
                autoFocus
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAsTemplate}
                  className="px-4 py-2 bg-[#C6A87C] text-white"
                >
                  Save Template
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Team Preview Panel Component
const TeamPreviewPanel = ({ preview, loading, getGradeColor }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="bg-gray-50 border border-gray-200 overflow-hidden"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`px-4 py-2 border-2 font-heading text-3xl ${getGradeColor(preview.team_grade)}`}>
            {loading ? '...' : preview.team_grade}
          </div>
          <div>
            <p className="font-semibold text-left">Team Grade Preview</p>
            <p className="text-sm text-gray-500">Score: {preview.team_score} • {preview.member_count} members</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-4 border-t border-gray-200">
              {/* Probability Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <span className="text-sm font-semibold">On-Time Probability</span>
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-heading">{preview.probability_on_time}%</span>
                    <span className="text-sm text-gray-500 mb-1">likelihood</span>
                  </div>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${preview.probability_on_time}%` }}
                    />
                  </div>
                </div>
                <div className="p-4 bg-white border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-green-500" />
                    <span className="text-sm font-semibold">Quality Probability</span>
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-heading">{preview.probability_quality}%</span>
                    <span className="text-sm text-gray-500 mb-1">likelihood</span>
                  </div>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 transition-all"
                      style={{ width: `${preview.probability_quality}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="p-4 bg-white border border-gray-100">
                <p className="text-xs font-semibold uppercase text-gray-500 mb-3">Score Breakdown</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-heading">{preview.score_breakdown.simple_average}</p>
                    <p className="text-xs text-gray-500">Simple Avg</p>
                  </div>
                  <div>
                    <p className="text-2xl font-heading">{preview.score_breakdown.weighted_average}</p>
                    <p className="text-xs text-gray-500">Weighted Avg</p>
                  </div>
                  <div>
                    <p className="text-2xl font-heading">{preview.score_breakdown.task_match_score}</p>
                    <p className="text-xs text-gray-500">Task Match</p>
                  </div>
                </div>
              </div>

              {/* Skills Coverage */}
              {preview.coverage_analysis && (
                <div className="p-4 bg-white border border-gray-100">
                  <p className="text-xs font-semibold uppercase text-gray-500 mb-3">Skills Coverage</p>
                  <div className="space-y-2">
                    {preview.coverage_analysis.covered?.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-600 font-semibold">Covered:</span>
                        {preview.coverage_analysis.covered.map(skill => (
                          <span key={skill} className="px-2 py-1 bg-green-50 text-green-700 text-xs">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                    {preview.coverage_analysis.missing?.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        <span className="text-xs text-orange-600 font-semibold">Missing:</span>
                        {preview.coverage_analysis.missing.map(skill => (
                          <span key={skill} className="px-2 py-1 bg-orange-50 text-orange-700 text-xs">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {preview.recommendations?.length > 0 && (
                <div className="p-4 bg-white border border-gray-100">
                  <p className="text-xs font-semibold uppercase text-gray-500 mb-3">Recommendations</p>
                  <ul className="space-y-2">
                    {preview.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <TrendingUp className="w-4 h-4 text-[#C6A87C] mt-0.5 flex-shrink-0" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Employee Modal Component
const EmployeeModal = ({ employee, onClose, onSave, api }) => {
  const [formData, setFormData] = useState({
    name: employee?.name || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
    role: employee?.role || 'Specialist',
    expertise: employee?.expertise || [],
    expertise_level: employee?.expertise_level || 'journeyman',
    temperament: employee?.temperament || [],
    years_experience: employee?.years_experience || 0,
    leadership_rating: employee?.leadership_rating || 3,
    certifications: employee?.certifications || [],
    licenses: employee?.licenses || [],
    hourly_rate: employee?.hourly_rate || 0,
    daily_rate: employee?.daily_rate || 0,
    ratings: employee?.ratings || {
      speed: 3.0,
      accuracy: 3.0,
      quality: 3.0,
      reliability: 3.0,
      teamwork: 3.0,
      communication: 3.0
    },
    // Personal Info
    profile_picture_url: employee?.profile_picture_url || '',
    date_of_birth: employee?.date_of_birth || '',
    age: employee?.age || '',
    birthplace: employee?.birthplace || '',
    citizenship: employee?.citizenship || 'US Citizen',
    address: employee?.address || '',
    city: employee?.city || '',
    state: employee?.state || '',
    zip_code: employee?.zip_code || '',
    // Emergency Contact
    emergency_contact: employee?.emergency_contact || {
      name: '',
      relationship: '',
      phone: '',
      email: '',
      address: ''
    },
    // Portal Access
    has_portal_access: employee?.has_portal_access || false,
    portal_password: ''
  });

  const [newCert, setNewCert] = useState('');
  const [newLicense, setNewLicense] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const citizenshipOptions = [
    'US Citizen',
    'Permanent Resident',
    'Work Visa (H-1B)',
    'Work Visa (H-2B)',
    'Work Authorization',
    'Other'
  ];

  const relationshipOptions = [
    'Spouse', 'Parent', 'Sibling', 'Child', 'Friend', 'Partner', 'Other'
  ];

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !employee?.id || !api) return;
    
    setUploadingPhoto(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await api.post(`/employees/${employee.id}/profile-picture`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, profile_picture_url: response.data.profile_picture_url }));
      toast.success('Profile picture uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const toggleExpertise = (exp) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(exp)
        ? prev.expertise.filter(e => e !== exp)
        : [...prev.expertise, exp]
    }));
  };

  const toggleTemperament = (temp) => {
    setFormData(prev => ({
      ...prev,
      temperament: prev.temperament.includes(temp)
        ? prev.temperament.filter(t => t !== temp)
        : [...prev.temperament, temp]
    }));
  };

  const addCertification = () => {
    if (newCert.trim() && !formData.certifications.includes(newCert.trim())) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCert.trim()]
      }));
      setNewCert('');
    }
  };

  const addLicense = () => {
    if (newLicense.trim() && !formData.licenses.includes(newLicense.trim())) {
      setFormData(prev => ({
        ...prev,
        licenses: [...prev.licenses, newLicense.trim()]
      }));
      setNewLicense('');
    }
  };

  const updateRating = (key, value) => {
    setFormData(prev => ({
      ...prev,
      ratings: { ...prev.ratings, [key]: value }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="font-heading text-xl">{employee ? 'Edit' : 'Add'} Team Member</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {[
            { id: 'basic', label: 'Basic Info' },
            { id: 'personal', label: 'Personal' },
            { id: 'emergency', label: 'Emergency' },
            { id: 'skills', label: 'Skills' },
            { id: 'ratings', label: 'Ratings' },
            { id: 'credentials', label: 'Credentials' },
            { id: 'portal', label: 'Portal Access' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-[#C6A87C] text-[#C6A87C]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              {/* Profile Picture */}
              <div className="flex items-center gap-6 mb-6">
                <div className="relative">
                  {formData.profile_picture_url ? (
                    <img 
                      src={formData.profile_picture_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.profile_picture_url}` : formData.profile_picture_url}
                      alt={formData.name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-[#C6A87C]/20"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Profile Picture</label>
                  {employee?.id ? (
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm hover:bg-gray-200 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      Upload Photo
                    </label>
                  ) : (
                    <p className="text-sm text-gray-500">Save employee first to upload photo</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    data-testid="employee-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
                  >
                    {roleOptions.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Hourly Rate</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Daily Rate</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      value={formData.daily_rate}
                      onChange={(e) => setFormData({ ...formData, daily_rate: parseFloat(e.target.value) || 0 })}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Personal Info Tab */}
          {activeTab === 'personal' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Date of Birth</label>
                  <input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Age</label>
                  <input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) || '' })}
                    min="18"
                    max="100"
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Birthplace</label>
                  <input
                    type="text"
                    value={formData.birthplace}
                    onChange={(e) => setFormData({ ...formData, birthplace: e.target.value })}
                    placeholder="City, State/Country"
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Citizenship / Work Status</label>
                <select
                  value={formData.citizenship}
                  onChange={(e) => setFormData({ ...formData, citizenship: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
                >
                  {citizenshipOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <h4 className="font-semibold mb-4">Home Address</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Street Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">City</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">State</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">ZIP Code</label>
                      <input
                        type="text"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Emergency Contact Tab */}
          {activeTab === 'emergency' && (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg mb-4">
                <p className="text-sm text-orange-800">
                  <strong>Important:</strong> Emergency contact information is kept confidential and used only in case of workplace emergencies.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Contact Name</label>
                  <input
                    type="text"
                    value={formData.emergency_contact?.name || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      emergency_contact: { ...formData.emergency_contact, name: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Relationship</label>
                  <select
                    value={formData.emergency_contact?.relationship || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      emergency_contact: { ...formData.emergency_contact, relationship: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
                  >
                    <option value="">Select...</option>
                    {relationshipOptions.map(rel => (
                      <option key={rel} value={rel}>{rel}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    value={formData.emergency_contact?.phone || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      emergency_contact: { ...formData.emergency_contact, phone: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.emergency_contact?.email || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      emergency_contact: { ...formData.emergency_contact, email: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Address (Optional)</label>
                <input
                  type="text"
                  value={formData.emergency_contact?.address || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    emergency_contact: { ...formData.emergency_contact, address: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                />
              </div>
            </div>
          )}

          {/* Skills Tab */}
          {activeTab === 'skills' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Years Experience</label>
                  <input
                    type="number"
                    value={formData.years_experience}
                    onChange={(e) => setFormData({ ...formData, years_experience: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Expertise Level</label>
                  <select
                    value={formData.expertise_level}
                    onChange={(e) => setFormData({ ...formData, expertise_level: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
                  >
                    {expertiseLevelOptions.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label} ({level.description})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Leadership Rating</label>
                <div className="flex gap-2 py-2">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setFormData({ ...formData, leadership_rating: rating })}
                      className="p-2"
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          rating <= formData.leadership_rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Expertise Areas</label>
                <div className="flex flex-wrap gap-2">
                  {expertiseOptions.map(exp => (
                    <button
                      key={exp}
                      type="button"
                      onClick={() => toggleExpertise(exp)}
                      className={`px-3 py-2 text-sm border transition-colors ${
                        formData.expertise.includes(exp)
                          ? 'bg-[#C6A87C] text-white border-[#C6A87C]'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-[#C6A87C]'
                      }`}
                    >
                      {exp}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Temperament / Traits</label>
                <div className="flex flex-wrap gap-2">
                  {temperamentOptions.map(temp => (
                    <button
                      key={temp}
                      type="button"
                      onClick={() => toggleTemperament(temp)}
                      className={`px-3 py-2 text-sm border transition-colors ${
                        formData.temperament.includes(temp)
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-purple-500'
                      }`}
                    >
                      {temp}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Performance Ratings Tab */}
          {activeTab === 'ratings' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-500 mb-4">
                Rate the employee's performance in each area (1-5 scale). These ratings affect their overall grade 
                and impact team calculations for on-time and quality probability.
              </p>
              
              {[
                { key: 'speed', label: 'Speed', description: 'How quickly they complete tasks', icon: Zap },
                { key: 'accuracy', label: 'Accuracy', description: 'Precision and attention to detail', icon: Target },
                { key: 'quality', label: 'Quality/Craftsmanship', description: 'Overall quality of work', icon: Award },
                { key: 'reliability', label: 'Reliability', description: 'Shows up on time, dependable', icon: Shield },
                { key: 'teamwork', label: 'Teamwork', description: 'Works well with others', icon: Users },
                { key: 'communication', label: 'Communication', description: 'Clear communication with team/clients', icon: FileText }
              ].map(rating => (
                <div key={rating.key} className="p-4 border border-gray-100 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <rating.icon className="w-5 h-5 text-[#C6A87C]" />
                      <span className="font-medium">{rating.label}</span>
                    </div>
                    <span className="text-2xl font-heading">{formData.ratings[rating.key].toFixed(1)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">{rating.description}</p>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-400 w-8">1.0</span>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      step="0.5"
                      value={formData.ratings[rating.key]}
                      onChange={(e) => updateRating(rating.key, parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#C6A87C]"
                    />
                    <span className="text-xs text-gray-400 w-8">5.0</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Credentials Tab */}
          {activeTab === 'credentials' && (
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Certifications</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newCert}
                    onChange={(e) => setNewCert(e.target.value)}
                    placeholder="Add certification..."
                    className="flex-1 px-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                  />
                  <button
                    type="button"
                    onClick={addCertification}
                    className="px-4 py-2 bg-[#C6A87C] text-white"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.certifications.map(cert => (
                    <span key={cert} className="px-3 py-1 bg-green-50 text-green-700 text-sm flex items-center gap-2">
                      <Award className="w-3 h-3" />
                      {cert}
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          certifications: prev.certifications.filter(c => c !== cert)
                        }))}
                        className="text-green-500 hover:text-green-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {formData.certifications.length === 0 && (
                    <p className="text-sm text-gray-400">No certifications added</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Licenses</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newLicense}
                    onChange={(e) => setNewLicense(e.target.value)}
                    placeholder="Add license..."
                    className="flex-1 px-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLicense())}
                  />
                  <button
                    type="button"
                    onClick={addLicense}
                    className="px-4 py-2 bg-[#C6A87C] text-white"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.licenses.map(license => (
                    <span key={license} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm flex items-center gap-2">
                      <Shield className="w-3 h-3" />
                      {license}
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          licenses: prev.licenses.filter(l => l !== license)
                        }))}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {formData.licenses.length === 0 && (
                    <p className="text-sm text-gray-400">No licenses added</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Portal Access Tab */}
          {activeTab === 'portal' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Portal Access:</strong> Enable portal access to allow this employee to log in and view their schedule, pay info, performance, and training opportunities.
                </p>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Portal Access</p>
                  <p className="text-sm text-gray-500">Allow this employee to log into the Employee Portal</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, has_portal_access: !prev.has_portal_access }))}
                  className={`relative w-14 h-8 rounded-full transition-colors ${
                    formData.has_portal_access ? 'bg-[#C6A87C]' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${
                    formData.has_portal_access ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {formData.has_portal_access && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                      {employee?.has_portal_access ? 'New Password (leave blank to keep current)' : 'Set Password *'}
                    </label>
                    <input
                      type="password"
                      value={formData.portal_password}
                      onChange={(e) => setFormData({ ...formData, portal_password: e.target.value })}
                      placeholder="Enter password"
                      className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium mb-2">Employee Login Credentials:</p>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">Employee ID:</span> <code className="bg-white px-2 py-1">{employee?.id || 'Will be generated'}</code></p>
                      <p><span className="text-gray-500">Login URL:</span> <code className="bg-white px-2 py-1">/employee/login</code></p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 mt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265]"
              data-testid="save-employee-btn"
            >
              {employee ? 'Update' : 'Add'} Team Member
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};
